// Business side: one client's account balance with the business. Entries are
// adjustments (never overwrites); a mistake is fixed by reversing an entry.
// Credit = you owe the client (prepaid or on account). Debit = the client owes you.
import React, { useCallback, useEffect, useState } from 'react';
import clientPortalAPI from './clientPortalApi';
import { money, day, balanceLabel, statusLabel } from './format';
import { formatApiError } from '../../utils/errorMessage';

export default function ClientBalancePanel({ businessId, client, onChanged }) {
  const [data, setData] = useState(null);
  const [direction, setDirection] = useState('credit');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const load = useCallback(async () => {
    try {
      setData((await clientPortalAPI.balance(businessId, client.id)).data);
      setInvoices((await clientPortalAPI.clientInvoices(businessId, client.id)).data.invoices || []);
    } catch (e) { setError(formatApiError(e, 'Could not load the balance')); }
  }, [businessId, client.id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, fallback) => {
    setBusy(true); setError('');
    try { await fn(); await load(); if (onChanged) onChanged(); } catch (e) { setError(formatApiError(e, fallback)); }
    setBusy(false);
  };

  return (
    <div className="border rounded p-2 mt-1 text-sm space-y-2" aria-label={`Balance for ${client.display_name || client.email}`}>
      {data && <p className="font-medium" data-testid="client-balance">{balanceLabel(data.balance)}</p>}
      <form className="flex flex-wrap gap-2 items-center" onSubmit={(e) => { e.preventDefault(); run(async () => { await clientPortalAPI.adjustBalance(businessId, client.id, { direction, amount, memo }); setAmount(''); setMemo(''); }, 'Could not save the entry'); }}>
        <select aria-label="Entry type" value={direction} onChange={(e) => setDirection(e.target.value)} className="border rounded px-1 py-1">
          <option value="credit">Credit (you owe the client)</option>
          <option value="debit">Debit (the client owes you)</option>
        </select>
        <input aria-label="Amount" inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="border rounded px-2 py-1 w-28" />
        <input aria-label="Note" required value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Why (required)" className="border rounded px-2 py-1" />
        <button type="submit" disabled={busy} className="border rounded px-3 py-1">Add entry</button>
      </form>
      {data && data.entries.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr><th className="text-left">Date</th><th className="text-left">Type</th><th className="text-right">Amount</th><th className="text-left">Note</th><th className="text-left">By</th><th /></tr></thead>
          <tbody>{data.entries.map((e) => (
            <tr key={e.id}>
              <td>{day(e.entry_date)}</td>
              <td>{e.kind === 'reversal' ? 'Correction' : e.direction === 'credit' ? 'Credit' : 'Debit'}</td>
              <td className="text-right">{money(e.amount)}</td>
              <td>{e.memo}</td>
              <td>{e.created_by_email || ''}</td>
              <td>{e.kind === 'adjustment' && !e.reversed && <button type="button" disabled={busy} className="text-red-600" onClick={() => run(() => clientPortalAPI.reverseBalanceEntry(businessId, client.id, e.id), 'Could not reverse this entry')}>Reverse</button>}</td>
            </tr>))}</tbody>
        </table>
      )}
      {invoices.length > 0 && (
        <table className="w-full text-xs" aria-label="Client invoices">
          <thead><tr><th className="text-left">Invoice</th><th className="text-left">Due</th><th className="text-right">Still owed</th><th className="text-left">Status</th><th /></tr></thead>
          <tbody>{invoices.map((i) => {
            const credit = data ? Number(data.balance) : 0;
            const owed = Number(i.amount_remaining);
            const canUse = credit > 0 && owed > 0 && ['sent', 'partially_paid', 'overdue'].includes(i.display_status);
            const use = Math.min(credit, owed).toFixed(2);
            return (
              <tr key={i.id}>
                <td>{i.invoice_number || i.id}</td><td>{day(i.due_date)}</td><td className="text-right">{money(i.amount_remaining)}</td>
                <td>{statusLabel(i.display_status)}</td>
                <td>
                  {i.display_status === 'draft' && <button type="button" disabled={busy} className="text-blue-600" onClick={() => run(() => clientPortalAPI.sendInvoice(businessId, i.id), 'Could not send this invoice')}>Send</button>}
                  {canUse && <button type="button" disabled={busy} className="text-blue-600" onClick={() => run(() => clientPortalAPI.applyCredit(businessId, client.id, i.id, use), 'Could not use the credit')}>Use {money(use)} credit</button>}
                </td>
              </tr>);
          })}</tbody>
        </table>
      )}
      {error && <p role="alert" className="text-red-600 text-xs">{error}</p>}
    </div>
  );
}
